import type { DependencyList } from "react";
import { createLocalPersister } from "tinybase/persisters/persister-browser/with-schemas";
import * as UIReact from "tinybase/ui-react/with-schemas";
import {
    type Value,
    type Values,
    type Store
} from "tinybase/with-schemas";
import { useSetTask } from "tinytick/ui-react";
import { client } from "@/utils/orpc";
import { useAuthValue } from "@/stores/auth-store";
import { toast } from "sonner";
import { useNetworkState } from "@uidotdev/usehooks";
import {
    USER_VALUES_SCHEMA,
    type UserSchema,
} from "@/stores/schemas";

const STORE_NAME = "user";

const {
    useCreatePersister,
    useProvideStore,
    useValue,
    useSetValueCallback,
    useValues,
    useStore,
} = UIReact as UIReact.WithSchemas<UserSchema>;

type ValueIDs = keyof typeof USER_VALUES_SCHEMA;

export const useUserStore = () => {
    return useStore();
}

export const useUserValue = <ValueID extends ValueIDs>(valueID: ValueID) => {
    return useValue(valueID, STORE_NAME);
}

export const useUserValues = (): Values<UserSchema[1]> => {
    return useValues(STORE_NAME);
}

export const useSetUserValueCallback = <Parameter, ValueID extends ValueIDs>
    (
        valueID: ValueID,
        getValue: (parameter: Parameter) => Value<UserSchema[1], ValueID>,
        getValueDeps?: DependencyList
    ) => {
    useSetValueCallback(valueID, getValue, getValueDeps, STORE_NAME);
}

export const UserStore = ({ store }: { store: Store<UserSchema> }) => {
    useCreatePersister(
        store,
        (store) => createLocalPersister(store, STORE_NAME),
        [],
        async (persister) => {
            await persister.startAutoLoad();
            await persister.startAutoSave();
        }
    );

    const network = useNetworkState();
    const accessToken = useAuthValue("accessToken");

    useSetTask(
        "getUserInfo",
        async () => {
            if (!network.online || !accessToken) {
                return;
            }

            try {

                const userInfo = await client.auth.getUserInfo({
                    accessToken: accessToken,
                });

                store.transaction(() => {
                    store.setValues({
                        id: userInfo.id,
                        name: userInfo.name!,
                        email: userInfo.email,
                        given_name: userInfo.given_name!,
                        family_name: userInfo.family_name!,
                        picture: userInfo.picture!,
                        roles: JSON.stringify(userInfo.roles) ?? "",
                    });
                });

            } catch (error) {
                console.error("[user] Error Getting User Info:", error);
                toast.error("Error Getting User Info");
                throw error;
            }
        },
        [network, accessToken]
    )

    useProvideStore(STORE_NAME, store);
    return null;
}